<script lang="ts">
  import { db } from '../../lib/firebase';
  import { authState } from '../../lib/auth.svelte';
  import {
    doc,
    collection,
    query,
    orderBy,
    onSnapshot,
    addDoc,
    updateDoc,
    Timestamp,
  } from 'firebase/firestore';
  import { onMount } from 'svelte';
  import {
    type Ticket,
    type TicketReply,
    TICKET_CATEGORIES,
    TICKET_PRIORITIES,
    TICKET_STATUSES,
  } from '../../lib/types';
  import { queueTicketNotification } from '../../lib/ticketNotifications';

  // Props
  type Props = {
    ticket: Ticket;
    onBack: () => void;
  };
  let { ticket, onBack }: Props = $props();

  // State
  let currentTicket = $state<Ticket>(ticket);
  let replies = $state<TicketReply[]>([]);
  let loadingReplies = $state(true);
  let replyMessage = $state('');
  let submittingReply = $state(false);
  let updatingNotifications = $state(false);
  let closingTicket = $state(false);
  let error = $state('');

  // Derived
  let canReply = $derived(
    currentTicket.status !== 'closed'
  );
  let canClose = $derived(
    currentTicket.status === 'resolved'
  );

  onMount(() => {
    // Listen to ticket changes
    const ticketRef = doc(db, 'support_tickets', ticket.id);
    const unsubTicket = onSnapshot(ticketRef, (snap) => {
      if (snap.exists()) {
        currentTicket = { id: snap.id, ...snap.data() } as Ticket;
      }
    });

    // Listen to replies
    const repliesRef = collection(db, 'support_tickets', ticket.id, 'replies');
    const q = query(repliesRef, orderBy('createdAt', 'asc'));
    const unsubReplies = onSnapshot(
      q,
      (snapshot) => {
        replies = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        } as TicketReply));
        loadingReplies = false;
      },
      (err) => {
        console.error('Error loading replies:', err);
        loadingReplies = false;
      }
    );

    return () => {
      unsubTicket();
      unsubReplies();
    };
  });

  async function handleSubmitReply(e: Event) {
    e.preventDefault();
    if (!replyMessage.trim() || !authState.user || submittingReply) return;

    submittingReply = true;
    error = '';

    try {
      const now = Timestamp.now();

      // Add reply
      await addDoc(collection(db, 'support_tickets', ticket.id, 'replies'), {
        authorId: authState.user.uid,
        authorName: authState.user.displayName || authState.user.email?.split('@')[0] || 'User',
        authorType: 'user',
        message: replyMessage.trim(),
        createdAt: now,
      });

      // Update ticket timestamp and potentially status
      const ticketRef = doc(db, 'support_tickets', ticket.id);
      const updates: any = { updatedAt: now };

      // If ticket was resolved, move to awaiting_response
      if (currentTicket.status === 'resolved') {
        updates.status = 'awaiting_response';
      }

      await updateDoc(ticketRef, updates);

      replyMessage = '';
    } catch (err: any) {
      console.error('Error submitting reply:', err);
      error = err.message || 'Failed to submit reply';
    } finally {
      submittingReply = false;
    }
  }

  async function toggleNotifications() {
    if (updatingNotifications) return;

    updatingNotifications = true;
    try {
      const ticketRef = doc(db, 'support_tickets', ticket.id);
      await updateDoc(ticketRef, {
        emailNotifications: !currentTicket.emailNotifications,
        updatedAt: Timestamp.now(),
      });
    } catch (err: any) {
      console.error('Error updating notifications:', err);
      error = err.message || 'Failed to update notification preference';
    } finally {
      updatingNotifications = false;
    }
  }

  async function closeTicket() {
    if (closingTicket || !canClose) return;

    closingTicket = true;
    try {
      const ticketRef = doc(db, 'support_tickets', ticket.id);
      await updateDoc(ticketRef, {
        status: 'closed',
        updatedAt: Timestamp.now(),
      });
    } catch (err: any) {
      console.error('Error closing ticket:', err);
      error = err.message || 'Failed to close ticket';
    } finally {
      closingTicket = false;
    }
  }

  function getStatusInfo(status: string) {
    return TICKET_STATUSES.find(s => s.id === status) || TICKET_STATUSES[0];
  }

  function getPriorityInfo(priority: string) {
    return TICKET_PRIORITIES.find(p => p.id === priority) || TICKET_PRIORITIES[1];
  }

  function getCategoryLabel(category: string) {
    return TICKET_CATEGORIES.find(c => c.id === category)?.label || category;
  }

  function formatDate(date: any): string {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString();
  }

  function formatDateShort(date: any): string {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  }
</script>

<div class="ticket-detail">
  <div class="detail-header">
    <button class="back-btn" onclick={onBack}>
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd" />
      </svg>
      Back to Tickets
    </button>
  </div>

  <div class="ticket-info">
    <div class="ticket-title-row">
      <h2>{currentTicket.subject}</h2>
      <span
        class="status-badge"
        style="background: {getStatusInfo(currentTicket.status).color}15; color: {getStatusInfo(currentTicket.status).color}"
      >
        {getStatusInfo(currentTicket.status).label}
      </span>
    </div>

    <div class="ticket-meta-row">
      <span class="meta-item">
        <strong>Category:</strong> {getCategoryLabel(currentTicket.category)}
      </span>
      <span class="meta-item">
        <strong>Priority:</strong>
        <span style="color: {getPriorityInfo(currentTicket.priority).color}">
          {getPriorityInfo(currentTicket.priority).label}
        </span>
      </span>
      <span class="meta-item">
        <strong>Created:</strong> {formatDateShort(currentTicket.createdAt)}
      </span>
    </div>

    <div class="ticket-description">
      <p>{currentTicket.description}</p>
    </div>

    <div class="ticket-actions">
      <label class="notification-toggle">
        <input
          type="checkbox"
          checked={currentTicket.emailNotifications}
          onchange={toggleNotifications}
          disabled={updatingNotifications}
        />
        <span>Email notifications</span>
      </label>

      {#if canClose}
        <button class="close-ticket-btn" onclick={closeTicket} disabled={closingTicket}>
          {closingTicket ? 'Closing...' : 'Close Ticket'}
        </button>
      {/if}
    </div>
  </div>

  {#if error}
    <div class="error-banner" role="alert">{error}</div>
  {/if}

  <div class="conversation">
    <h3>Conversation</h3>

    {#if loadingReplies}
      <div class="loading-replies">
        <div class="spinner"></div>
        <span>Loading replies...</span>
      </div>
    {:else if replies.length === 0}
      <div class="no-replies">
        <p>No replies yet. Our support team will respond soon.</p>
      </div>
    {:else}
      <div class="replies-list">
        {#each replies as reply (reply.id)}
          <div class="reply" class:support={reply.authorType === 'support'}>
            <div class="reply-header">
              <span class="reply-author">
                {reply.authorName}
                {#if reply.authorType === 'support'}
                  <span class="support-badge">Support</span>
                {/if}
              </span>
              <span class="reply-date">{formatDate(reply.createdAt)}</span>
            </div>
            <div class="reply-message">
              {reply.message}
            </div>
          </div>
        {/each}
      </div>
    {/if}

    {#if canReply}
      <form class="reply-form" onsubmit={handleSubmitReply}>
        <textarea
          bind:value={replyMessage}
          placeholder="Type your reply..."
          rows="3"
          disabled={submittingReply}
        ></textarea>
        <button type="submit" disabled={submittingReply || !replyMessage.trim()}>
          {submittingReply ? 'Sending...' : 'Send Reply'}
        </button>
      </form>
    {:else}
      <div class="ticket-closed-notice">
        <p>This ticket is closed. No further replies can be added.</p>
      </div>
    {/if}
  </div>
</div>

<style>
  .ticket-detail {
    background: white;
    border-radius: 12px;
    overflow: hidden;
  }

  .detail-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .back-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: #667eea;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
  }

  .back-btn:hover {
    text-decoration: underline;
  }

  .back-btn svg {
    width: 20px;
    height: 20px;
  }

  .ticket-info {
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .ticket-title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .ticket-title-row h2 {
    margin: 0;
    font-size: 1.25rem;
    color: #333;
    flex: 1;
  }

  .status-badge {
    font-size: 0.8rem;
    font-weight: 500;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    flex-shrink: 0;
  }

  .ticket-meta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    font-size: 0.875rem;
    color: #666;
    margin-bottom: 1rem;
  }

  .meta-item strong {
    color: #333;
  }

  .ticket-description {
    background: #f9fafb;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .ticket-description p {
    margin: 0;
    color: #333;
    white-space: pre-wrap;
    line-height: 1.6;
  }

  .ticket-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .notification-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #666;
    cursor: pointer;
  }

  .notification-toggle input {
    width: 16px;
    height: 16px;
    accent-color: #667eea;
  }

  .close-ticket-btn {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    color: #374151;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .close-ticket-btn:hover:not(:disabled) {
    background: #e5e7eb;
  }

  .close-ticket-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .error-banner {
    background: #fee2e2;
    border-bottom: 1px solid #fecaca;
    color: #dc2626;
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
  }

  .conversation {
    padding: 1.5rem;
  }

  .conversation h3 {
    margin: 0 0 1rem;
    font-size: 1rem;
    color: #333;
  }

  .loading-replies {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    color: #666;
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #e5e7eb;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .no-replies {
    background: #f9fafb;
    border-radius: 8px;
    padding: 1.5rem;
    text-align: center;
  }

  .no-replies p {
    margin: 0;
    color: #666;
  }

  .replies-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .reply {
    background: #f0f4ff;
    border-radius: 8px;
    padding: 1rem;
    border-left: 3px solid #667eea;
  }

  .reply.support {
    background: #f0fdf4;
    border-left-color: #22c55e;
  }

  .reply-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .reply-author {
    font-weight: 500;
    color: #333;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .support-badge {
    background: #22c55e;
    color: white;
    font-size: 0.7rem;
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    font-weight: 500;
  }

  .reply-date {
    font-size: 0.8rem;
    color: #666;
  }

  .reply-message {
    color: #333;
    line-height: 1.5;
    white-space: pre-wrap;
  }

  .reply-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .reply-form textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.9375rem;
    resize: vertical;
    min-height: 80px;
  }

  .reply-form textarea:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  .reply-form button {
    align-self: flex-end;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .reply-form button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .reply-form button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .ticket-closed-notice {
    background: #f3f4f6;
    border-radius: 8px;
    padding: 1rem;
    text-align: center;
  }

  .ticket-closed-notice p {
    margin: 0;
    color: #666;
  }

  @media (max-width: 480px) {
    .ticket-title-row {
      flex-direction: column;
    }

    .ticket-meta-row {
      flex-direction: column;
      gap: 0.5rem;
    }

    .ticket-actions {
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start;
    }
  }
</style>
